/*
<summary>
   This functional component is all about rendering the Step 2 User Created Inspection tabs (title, image upload, drag/drop, tab reordering) and hosting the check items panel for the active tab.
   Developer: Aashish Singh, Created Date: 20-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { useFormikContext } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import { useStore } from '../../../../contexts/store-provider';
import { getUserId } from '../../../../helpers/localstorage-helper';
import { IToolCheckItem, IToolInspectionTab, IToolTabImage } from '../../../../models/forms/IToolInspection';
import { IToolManagementVM } from '../../../../models/response/IToolManagementResponse';
import FormLoader from '../../../../shared-components/FormLoader/FormLoader';
import { DeleteIcon, PlusIcon, UploadDocumentIcon } from '../../../../shared-components/svgs/svgs';
import { formatMessage } from '../../../../translations/format-message';
import ToolCheckItemsPanel from './tool-check-items-panel';
import {
  hasEmptyTabTitle,
  hasNoImage,
  isDuplicateTabTitle,
} from './step-two-validation';

interface IProps {
  submitAttempted?: boolean;
}

const StepTwoUserCreatedInspection = ({ submitAttempted }: IProps) => {
  const { values, setFieldValue } = useFormikContext<IToolManagementVM>();
  const { toolManagementStore } = useStore();
  const { AddToolTempFilesService } = toolManagementStore;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const tabs: IToolInspectionTab[] = values.InspectionTabs || [];
  const [activeTabIndex, setActiveTabIndex] = useState<number>(
    tabs[0]?.TabIndex ?? 1
  );
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingPreview, setPendingPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [titleTouchedMap, setTitleTouchedMap] = useState<Record<number, boolean>>({});
  const [dragOverTabIndex, setDragOverTabIndex] = useState<number | null>(null);
  const [isDropzoneDragOver, setIsDropzoneDragOver] = useState<boolean>(false);

  /**
   * This useEffect is used to auto-initialize a blank Tab 1 when no tabs exist yet on landing on Step 2.
   */
  useEffect(() => {
    if (!values.InspectionTabs || values.InspectionTabs.length === 0) {
      const initialTab: IToolInspectionTab = {
        TabIndex: 1,
        Title: '',
        Images: [],
        CheckItems: [],
      };
      setFieldValue('InspectionTabs', [initialTab]);
      setActiveTabIndex(1);
    }
  }, [values.InspectionTabs, setFieldValue]);

  /**
   * This useEffect is used to fall back the active tab to the first tab whenever the currently active tab no longer exists.
   */
  useEffect(() => {
    if (tabs.length > 0 && !tabs.some((t) => t.TabIndex === activeTabIndex)) {
      setActiveTabIndex(tabs[0].TabIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs.length]);

  /**
   * This useEffect is used to clear the pending image file and its preview, along with the file input, whenever the active tab changes.
   */
  useEffect(() => {
    setPendingFile(null);
    setPendingPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [activeTabIndex]);

  const activeTab = tabs.find((t) => t.TabIndex === activeTabIndex);
  const isTitleTouched = (tabIndex: number) =>
    !!submitAttempted || !!titleTouchedMap[tabIndex];
  const markTitleTouched = (tabIndex: number) => {
    setTitleTouchedMap((prev) => ({ ...prev, [tabIndex]: true }));
  };

  /**
   * This function is used to add a new blank inspection tab, up to a maximum of 5 tabs.
   */
  const handleAddTab = () => {
    if (tabs.length >= 5) {
      toast.error(formatMessage('max_tabs_allowed'));
      return;
    }
    const nextIndex =
      tabs.reduce((max, t) => Math.max(max, t.TabIndex), 0) + 1;
    const newTab: IToolInspectionTab = {
      TabIndex: nextIndex,
      Title: '',
      Images: [],
      CheckItems: [],
    };
    setFieldValue('InspectionTabs', [...tabs, newTab]);
    setActiveTabIndex(nextIndex);
  };

  /**
   * This function is used to remove an inspection tab and, if it was the active one, fall back to the first remaining tab.
   * @param tabIndex: Tab index to remove
   * @param e: Click event, stopped so it doesn't also select the tab
   */
  const handleRemoveTab = (
    tabIndex: number,
    e: React.MouseEvent<HTMLSpanElement>
  ) => {
    e.stopPropagation();
    const remaining = tabs.filter((t) => t.TabIndex !== tabIndex);
    setFieldValue('InspectionTabs', remaining);
    if (remaining.length > 0) {
      setActiveTabIndex(remaining[0].TabIndex);
    }
  };

  /**
   * This function is used to reorder tabs after a drag-and-drop, then renumber every tab's TabIndex to match the new order.
   * @param draggedTabIndex: Tab index that was dragged
   * @param targetTabIndex: Tab index it was dropped onto
   */
  const handleReorderTabs = (draggedTabIndex: number, targetTabIndex: number) => {
    if (draggedTabIndex === targetTabIndex) return;

    const list = [...tabs];
    const originalActiveTab = list.find((t) => t.TabIndex === activeTabIndex);
    const draggedIdx = list.findIndex((t) => t.TabIndex === draggedTabIndex);
    const originalTargetIdx = list.findIndex((t) => t.TabIndex === targetTabIndex);
    if (draggedIdx < 0 || originalTargetIdx < 0) return;

    const draggedItem = list[draggedIdx];
    const isMovingForward = draggedIdx < originalTargetIdx;

    list.splice(draggedIdx, 1);
    const targetIdx = list.findIndex((t) => t.TabIndex === targetTabIndex);
    if (targetIdx < 0) return;
    const insertAt = isMovingForward ? targetIdx + 1 : targetIdx;
    list.splice(insertAt, 0, draggedItem);

    if (originalActiveTab) {
      const newActiveIdx = list.indexOf(originalActiveTab);
      if (newActiveIdx >= 0) {
        setActiveTabIndex(newActiveIdx + 1);
      }
    }

    setFieldValue(
      'InspectionTabs',
      list.map((t, idx) => ({ ...t, TabIndex: idx + 1 }))
    );
  };

  /**
   * This function is used to update the title of the currently active tab.
   * @param value: New tab title
   */
  const handleTitleChange = (value: string) => {
    setFieldValue(
      'InspectionTabs',
      tabs.map((t) =>
        t.TabIndex === activeTabIndex ? { ...t, Title: value } : t
      )
    );
  };

  const MAX_SIZE_MB = 10;
  const MAX_FILE_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024; // 10MB

  /**
   * This function is used to stage a picked/dropped/pasted image file and build its data-URL preview.
   * @param file: Image file to stage
   */
  const handleFileSelected = (file: File) => {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`File size should not exceed ${MAX_SIZE_MB} MB.`);
      return;
    }
    setPendingFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setPendingPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleDropzoneDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDropzoneDragOver(true);
  };

  const handleDropzoneDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDropzoneDragOver(false);
  };

  /**
   * This function is used to accept a dropped image file onto the dropzone and stage it for upload.
   * @param e: Drop event carrying the dropped file(s)
   */
  const handleDropzoneDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDropzoneDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith('image/')) {
        handleFileSelected(file);
      }
    }
  };

  /**
   * This function is used to accept a pasted image from the clipboard and stage it for upload.
   * @param e: Clipboard paste event
   */
  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          handleFileSelected(file);
          e.preventDefault();
          break;
        }
      }
    }
  };

  /**
   * This function is used to validate the active tab (title, duplicate title, image present) and upload the staged image, adding it to the tab's images once the upload succeeds.
   */
  const handleSaveImage = async () => {
    if (!activeTab) return;

    markTitleTouched(activeTabIndex);

    if (hasEmptyTabTitle(activeTab)) {
      toast.error(formatMessage('please_enter_tab_title'));
      return;
    }

    if (isDuplicateTabTitle(activeTab, tabs)) {
      toast.error(formatMessage('duplicate_found_tabs'));
      return;
    }

    if (!pendingFile && hasNoImage(activeTab)) {
      toast.error(formatMessage('please_add_tab_image'));
      return;
    }

    if (!pendingFile || !pendingPreview) return;

    if (pendingFile.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`File size should not exceed ${MAX_SIZE_MB} MB.`);
      return;
    }

    try {
      setIsUploading(true);
      const createdBy = Number(getUserId()) || 1;
      const res = await AddToolTempFilesService(pendingFile, createdBy);

      const fileId =
        typeof res === 'number'
          ? res
          : res?.Id ?? res?.FileId ?? res?.fileId ?? 0;
      const s3FilePath = res?.S3FilePath || '';
      const filePath = res?.FilePath || '';
      const fileName = res?.FileName || pendingFile.name;
      const previewUrl = s3FilePath || pendingPreview || filePath;

      const newImage: IToolTabImage = {
        FileId: fileId,
        FileName: fileName,
        FileType: pendingFile.type,
        PreviewUrl: previewUrl,
        FilePath: filePath,
        S3FilePath: s3FilePath,
      };

      setFieldValue(
        'InspectionTabs',
        tabs.map((t) =>
          t.TabIndex === activeTabIndex
            ? {
                ...t,
                Images: [...t.Images, newImage],
              }
            : t
        )
      );
      setPendingFile(null);
      setPendingPreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Error uploading temp file:', err);
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * This function is used to remove a saved image from the active tab by its index.
   * @param imgIndex: Image index to remove
   * @param e: Click event, stopped so it doesn't also select the tab
   */
  const handleRemoveImage = (
    imgIndex: number,
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();
    setFieldValue(
      'InspectionTabs',
      tabs.map((t) =>
        t.TabIndex === activeTabIndex
          ? { ...t, Images: t.Images.filter((_, i) => i !== imgIndex) }
          : t
      )
    );
  };

  /**
   * This function is used to persist the check items (and their headers) coming from the check items panel back onto the active tab.
   * @param checkItems: Flat list of check items
   * @param headers: Grouped header/items structure
   */
  const handleCheckItemsChange = (
    checkItems: IToolCheckItem[],
    headers?: { Title: string; Items: IToolCheckItem[] }[]
  ) => {
    setFieldValue(
      'InspectionTabs',
      (values.InspectionTabs || []).map((t) =>
        t.TabIndex === activeTabIndex
          ? { ...t, CheckItems: checkItems, Headers: headers }
          : t
      )
    );
  };

  const activeTabTitleTouched = activeTab ? isTitleTouched(activeTab.TabIndex) : false;
  const activeTabHasDuplicateTitle = activeTab ? isDuplicateTabTitle(activeTab, tabs) : false;

  return (
    <div className="tool-inspection-step d-flex gap-3" onPaste={handlePaste}>
      {isUploading && <FormLoader loading={isUploading} contained />}
      {/* This is the left outer panel holding tabs, title and image upload. */}
      <div className="tool-inspection-left-panel rounded-3 bg-light d-flex flex-column h-100 overflow-hidden">
        <div className="tool-pic-name-list d-flex flex-wrap align-items-center rounded p-1">
          {tabs.map((tab) => {
            return (
              <div
                key={tab.TabIndex}
                title={tab.Title}
                draggable
                className={`tool-tab-block d-flex align-items-center draggable-tab show-Elipses ${tab.TabIndex === activeTabIndex ? 'active text-white' : ''} ${dragOverTabIndex === tab.TabIndex ? 'drag-over' : ''}`}
                onClick={() => setActiveTabIndex(tab.TabIndex)}
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/plain', String(tab.TabIndex));
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOverTabIndex(tab.TabIndex);
                }}
                onDragLeave={() => setDragOverTabIndex(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOverTabIndex(null);
                  const draggedId = Number(e.dataTransfer.getData('text/plain'));
                  if (draggedId && draggedId !== tab.TabIndex) {
                    handleReorderTabs(draggedId, tab.TabIndex);
                  }
                }}
              >
                <span className="tool-tab-block-name show-Elipses">
                  {tab.Title || <FormattedMessage id="tab_title" defaultMessage="Tab Title" />}
                </span>
                <span
                  className="tool-tab-block-close d-flex align-items-center"
                  onClick={(e) => handleRemoveTab(tab.TabIndex, e)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    fill="currentColor"
                    className="bi bi-x"
                    viewBox="0 0 16 16"
                  >
                    <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                  </svg>
                </span>
              </div>
            );
          })}
          <div
            className="tool-tab-block-add d-flex align-items-center justify-content-center flex-shrink-0 text-white"
            onClick={handleAddTab}
            title={formatMessage('add_tab')}
          >
            <PlusIcon />
          </div>
        </div>

        {activeTab ? (
          <div className="tool-tab-body d-flex overflow-hidden">
            {/* This sub-column holds the tab title input and image upload controls. */}
            <div className="tool-tab-controls d-flex flex-column">
              <div className="tool-tab-heading-group mt-0">
                <input
                  className="form-control tool-tab-title-input w-100"
                  placeholder={formatMessage('tab_title')}
                  value={activeTab.Title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  maxLength={100}
                />
              </div>

              <div className="tool-tab-image-box rounded-3 bg-white overflow-hidden">
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      handleFileSelected(e.target.files[0]);
                    }
                  }}
                />
                <div
                  className={`tool-tab-dropzone h-100 p-2 ${isDropzoneDragOver ? 'drag-over' : ''}`}
                  onClick={() => !isUploading && fileInputRef.current?.click()}
                  onDragOver={handleDropzoneDragOver}
                  onDragLeave={handleDropzoneDragLeave}
                  onDrop={handleDropzoneDrop}
                >
                  {pendingPreview ? (
                    <div className="tool-tab-drag-preview position-relative w-100 h-100 rounded overflow-hidden">
                      <img
                        className="w-100 h-100 object-fit-contain"
                        src={pendingPreview}
                        alt={pendingFile?.name}
                      />
                      <div className="tool-tab-drag-overlay position-absolute bottom-0 start-0 end-0 text-center text-white">
                        <FormattedMessage id="drag_drop" />{' '}
                        <a className="text-decoration-underline text-white"><FormattedMessage id="click_browse" /></a>
                      </div>
                    </div>
                  ) : (
                    <div className="tool-tab-drag-box h-100 d-flex flex-column align-items-center justify-content-center text-center rounded">
                      <UploadDocumentIcon />
                      <small>
                        <span className="text-dark"><FormattedMessage id="drag_drop" /></span>{' '}
                        <a className="text-decoration-underline"><FormattedMessage id="click_browse" /></a>
                      </small>
                    </div>
                  )}
                </div>
              </div>
              <div className="text-center tool-tab-save-wrap mt-2">
                <Button
                  type="button"
                  variant="primary"
                  className="btn-save-primary px-4"
                  onClick={handleSaveImage}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      <FormattedMessage id="uploading" defaultMessage="Uploading..." />
                    </>
                  ) : (
                    <FormattedMessage id="save" />
                  )}
                </Button>
              </div>
            </div>

            {/* This sub-column shows the saved images / preview area. */}
            <div className="tool-inspection-preview rounded-3 bg-white d-flex align-items-start justify-content-center overflow-y-auto h-100 mh-100">
              {activeTab.Images.length > 0 ? (
                <div className="tool-tab-saved-images d-flex flex-column w-100">
                  {activeTab.Images.map((img, idx) => (
                    <div
                      key={idx}
                      className="tool-tab-saved-image position-relative rounded overflow-hidden bg-white"
                    >
                      <img
                        className="w-100 d-block object-fit-contain"
                        src={img.S3FilePath || img.PreviewUrl || img.FilePath}
                        alt={img.FileName}
                      />
                      <button
                        type="button"
                        className="tool-tab-saved-image-remove position-absolute border-0 rounded-circle bg-white d-flex align-items-center justify-content-center"
                        onClick={(e) => handleRemoveImage(idx, e)}
                      >
                        <DeleteIcon />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="tool-tab-preview-empty h-100 d-flex align-items-center justify-content-center">
                  <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#d0d5dd" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                    <line x1="3" y1="3" x2="21" y2="21" />
                  </svg>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="tool-tab-empty text-center"><FormattedMessage id="no_tabs_added" /></div>
        )}
      </div>

      {/* This is the right outer panel holding the check items for the active tab. */}
      <div className="tool-inspection-checkitems rounded-3 bg-white d-flex flex-column h-100 overflow-hidden">
        <div className="tool-checkitems-top-tab">
          <span className="checkitems-tab-title d-inline-block bg-white fw-semibold"><FormattedMessage id="check_items" defaultMessage="Check Item" /></span>
        </div>
        <div className="tool-checkitems-container d-flex flex-column overflow-scroll">
          {!activeTab ? (
            <div className="tool-checkitems-empty text-center">
              <FormattedMessage id="no_tabs_added" />
            </div>
          ) : hasNoImage(activeTab) ? (
            <div className="tool-checkitems-empty text-center flex-grow-1 d-flex justify-content-center align-items-center">
              <FormattedMessage id="add_image_to_proceed_with_check_items" defaultMessage="Please add an image to proceed with check items." />
            </div>
          ) : (
            <ToolCheckItemsPanel
              key={activeTab.TabIndex}
              checkItems={activeTab.CheckItems}
              headers={activeTab.Headers}
              onChange={handleCheckItemsChange}
              submitAttempted={submitAttempted}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default StepTwoUserCreatedInspection;
